/**
 * src/lib/hooks/useProcurementData.js — Procurement domain hook.
 *
 * Owns: suppliers, purchaseOrders state,
 *       all procurement mutation callbacks.
 *
 * Extracted from useAppData.js (Phase 81-01).
 */

import { useState, useCallback } from 'react';
import { fetchSuppliers, fetchPurchaseOrders, createSupplier, updateSupplier, createPurchaseOrder } from '../api/procurement';
import { logAction, logResult, logMergeDecision } from '../observability';
import { logAppError } from '../errorReporter';
import { mergeWithOptimistic } from '../mergeWithOptimistic';
import { reconcileCollection } from '../offline/entityCache';
import { getOnline } from '../offline/networkStatus';
import { maybeEnqueueOffline, newOfflineId } from '../offline/enqueueOffline';

export function useProcurementData({ scheduleReload, offlineEnabled = false }) {
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  const handleCreateSupplier = useCallback(async (data) => {
    const cid = logAction('procurement:createSupplier', { name: data.name });

    if (offlineEnabled && !getOnline()) {
      const offlineId = newOfflineId();
      const realRow = { ...data, id: offlineId, _pendingSync: true };
      setSuppliers(prev => [realRow, ...prev]);
      await maybeEnqueueOffline({ offlineEnabled, domain: 'procurement', type: 'create_supplier', entity_id: offlineId, payload: { ...data, id: offlineId } });
      logResult('procurement:createSupplier', 'queued', { correlationId: cid });
      return { success: true, queued: true, record: realRow };
    }

    try {
      const result = await createSupplier(data);
      if (result?.success) {
        logResult('procurement:createSupplier', 'success', { correlationId: cid });
        scheduleReload(500, 'createSupplier');
      }
      return result;
    } catch (e) {
      logAppError(e, 'useProcurementData:createSupplier');
      logResult('procurement:createSupplier', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, [scheduleReload, offlineEnabled]);

  const handleUpdateSupplier = useCallback(async (id, data) => {
    const cid = logAction('procurement:updateSupplier', { id });

    if (offlineEnabled && !getOnline()) {
      setSuppliers(prev => prev.map(s => s.id === id ? { ...s, ...data, _pendingSync: true } : s));
      await maybeEnqueueOffline({ offlineEnabled, domain: 'procurement', type: 'update_supplier', entity_id: id, payload: data });
      logResult('procurement:updateSupplier', 'queued', { correlationId: cid });
      return { success: true, queued: true };
    }

    try {
      const result = await updateSupplier(id, data);
      if (result?.success) {
        logResult('procurement:updateSupplier', 'success', { correlationId: cid });
        scheduleReload(500, 'updateSupplier');
      }
      return result;
    } catch (e) {
      logAppError(e, 'useProcurementData:updateSupplier');
      logResult('procurement:updateSupplier', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, [scheduleReload, offlineEnabled]);

  const handleCreatePurchaseOrder = useCallback(async (data) => {
    const cid = logAction('procurement:createPurchaseOrder', {});

    if (offlineEnabled && !getOnline()) {
      const offlineId = newOfflineId();
      const realRow = { ...data, id: offlineId, _pendingSync: true };
      setPurchaseOrders(prev => [realRow, ...prev]);
      await maybeEnqueueOffline({ offlineEnabled, domain: 'procurement', type: 'create_po', entity_id: offlineId, payload: { ...data, id: offlineId } });
      logResult('procurement:createPurchaseOrder', 'queued', { correlationId: cid });
      return { success: true, queued: true, record: realRow };
    }

    try {
      const result = await createPurchaseOrder(data);
      if (result?.success) {
        logResult('procurement:createPurchaseOrder', 'success', { correlationId: cid });
        scheduleReload(500, 'createPurchaseOrder');
      }
      return result;
    } catch (e) {
      logAppError(e, 'useProcurementData:createPurchaseOrder');
      logResult('procurement:createPurchaseOrder', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, [scheduleReload, offlineEnabled]);

  // ─── Fetch / Apply / Merge (Phase 81-02) ───────────────────
  // Offline-tasks (flag-gated): write-through to the local cache when online,
  // hydrate from it when offline. Same pattern as projects/crm/directory.
  const fetchAll = useCallback(async () => {
    const [suppliers, purchaseOrders] = await Promise.all([
      fetchSuppliers().catch(e => { console.warn('[ProcurementData] Suppliers failed:', e.message); return []; }),
      fetchPurchaseOrders().catch(e => { console.warn('[ProcurementData] POs failed:', e.message); return []; }),
    ]);
    if (offlineEnabled) {
      const online = getOnline();
      const [s, po] = await Promise.all([
        reconcileCollection('suppliers', suppliers, online),
        reconcileCollection('purchaseOrders', purchaseOrders, online),
      ]);
      return { suppliers: s, purchaseOrders: po };
    }
    return { suppliers, purchaseOrders };
  }, [offlineEnabled]);

  const applyFetchResult = useCallback((data) => {
    setSuppliers(Array.isArray(data.suppliers) ? data.suppliers : []);
    setPurchaseOrders(Array.isArray(data.purchaseOrders) ? data.purchaseOrders : []);
  }, []);

  const mergeAll = useCallback((data, optimisticTimestamps, now) => {
    const merge = (setter, newData) => {
      if (!newData) return;
      setter(prev => {
        const { merged, anyChanged, expiredIds } = mergeWithOptimistic(prev, newData, optimisticTimestamps, now);
        for (const { id, age_ms } of expiredIds) logMergeDecision(id, 'ttl_expired', { age_ms });
        return anyChanged ? merged : prev;
      });
    };
    merge(setSuppliers, data.suppliers);
    merge(setPurchaseOrders, data.purchaseOrders);
  }, []);

  return {
    suppliers, setSuppliers,
    purchaseOrders, setPurchaseOrders,
    handleCreateSupplier,
    handleUpdateSupplier,
    handleCreatePurchaseOrder,
    fetchAll,
    applyFetchResult,
    mergeAll,
  };
}
